import { useQueryClient } from "@tanstack/react-query";
import { DateTime } from "luxon";
import { useCallback, useMemo } from "react";

import { QUERY_KEYS } from "@/constants";
import { useMovePassengerToTaskMutation } from "@/requests/tracking.mutations";
import { TrackingDragPayload } from "@/stores/use-tracking-dnd";
import { IPlanPassenger, Task } from "@/types/tasks.types";

export function useTrackingBoardDnd({ date }: { date: DateTime }) {
    const queryClient = useQueryClient();
    const { mutateAsync: movePassengerMutation, isPending } = useMovePassengerToTaskMutation();
    const queryKey = useMemo(() => [QUERY_KEYS.GET_TASKS, date.toISODate()], [date]);

    const movePassenger = useCallback(
        ({ dragData, targetTaskId }: { dragData: TrackingDragPayload; targetTaskId: string }) => {
            if (!dragData || !targetTaskId || targetTaskId === dragData.taskId || isPending) return;

            queryClient.setQueryData<Task<IPlanPassenger<"api">>[]>(queryKey, (prev) => {
                if (!prev) return prev;

                let passenger: IPlanPassenger<"api"> | undefined;
                const withoutPassenger = prev.map((task) => {
                    if (task.id !== dragData.taskId) return task;
                    const found = task.passengers.find((p) => p.id === dragData.id);
                    if (found) passenger = found;
                    return { ...task, passengers: task.passengers.filter((p) => p.id !== dragData.id) };
                });
                if (!passenger) return prev;
                const movedPassenger: IPlanPassenger<"api"> = { ...passenger, taskId: targetTaskId };

                return withoutPassenger.map((task) =>
                    task.id === targetTaskId ? { ...task, passengers: [...task.passengers, movedPassenger] } : task
                );
            });

            movePassengerMutation({
                passengerId: dragData.id,
                fromTaskId: dragData.taskId,
                toTaskId: targetTaskId,
            }).catch(() => {
                queryClient.invalidateQueries({ queryKey });
            });
        },
        [queryClient, queryKey, movePassengerMutation, isPending]
    );

    return { movePassenger, isPending };
}
